"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";

import { cn } from "~/lib/utils";

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "ekv:data-[state=open]:animate-in ekv:data-[state=closed]:animate-out ekv:data-[state=closed]:fade-out-0 ekv:data-[state=open]:fade-in-0 ekv:fixed ekv:inset-0 ekv:z-50 ekv:bg-black/50",
        className,
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean;
}) {
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          "ekv:bg-background ekv:data-[state=open]:animate-in ekv:data-[state=closed]:animate-out ekv:data-[state=closed]:fade-out-0 ekv:data-[state=open]:fade-in-0 ekv:data-[state=closed]:zoom-out-95 ekv:data-[state=open]:zoom-in-95 ekv:fixed ekv:top-[50%] ekv:left-[50%] ekv:z-50 ekv:grid ekv:w-full ekv:max-w-[calc(100%-2rem)] ekv:translate-x-[-50%] ekv:translate-y-[-50%] ekv:gap-4 ekv:rounded-lg ekv:border ekv:p-6 ekv:shadow-lg ekv:duration-200 ekv:outline-none ekv:sm:max-w-lg",
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className="ekv:ring-offset-background ekv:focus:ring-ring ekv:data-[state=open]:bg-accent ekv:data-[state=open]:text-muted-foreground ekv:absolute ekv:top-4 ekv:right-4 ekv:rounded-xs ekv:opacity-70 ekv:transition-opacity ekv:hover:opacity-100 ekv:focus:ring-2 ekv:focus:ring-offset-2 ekv:focus:outline-hidden ekv:disabled:pointer-events-none ekv:[&_svg]:pointer-events-none ekv:[&_svg]:shrink-0 ekv:[&_svg:not([class*=size-])]:size-4"
          >
            <XIcon />
            <span className="ekv:sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn(
        "ekv:flex ekv:flex-col ekv:gap-2 ekv:text-center ekv:sm:text-left",
        className,
      )}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "ekv:flex ekv:flex-col-reverse ekv:gap-2 ekv:sm:flex-row ekv:sm:justify-end",
        className,
      )}
      {...props}
    />
  );
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn(
        "ekv:text-lg ekv:leading-none ekv:font-semibold",
        className,
      )}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("ekv:text-muted-foreground ekv:text-sm", className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
